import { flow, SnapshotIn, toGenerator, types } from 'mobx-state-tree'
import { FetchModel } from '../models/FetchModel'
import {
    OperationFailure,
    OperationInProgress,
    OperationNotStarted,
    OperationStatusModel,
    OperationSuccess,
  } from '../models/common/OperationStatus'
import { MeasurementUnitsService } from '../models/MeasurementUnitsService'
import { MeasurementUnitInstances, MeasurementUnitsEnum, MeasurementUnitsModel } from '../models/CenterSettings/MeasurementUnitModel'
import { PermissionTypes, UserAndPermissionsModel, UserPermissionsKeyTypes } from '../models/Users/UserModel'
import { OversightDeploymentModel } from 'models/OversightDeploymentSettings/OversightDeploymentModel'
import { ControlMode } from '../models/CenterSettings/ControlModeModel'
import { DEFAULT_CONTROL_MODE, SHOW_CONTROL_MODE_TOGGLE,CENTER_NAME } from '../params'
import { isOversightControl } from './rootStore'

const CONTROL_MODE_STORAGE_KEY = 'controlMode'

// A Store for settings used by several pages - like center location, center measurement units etc.
export const CommonSettingsStore = FetchModel.named('CommonSettingsStore')
  .props({
    deploymentId: types.maybe(types.number),
    deploymentName: types.maybe(types.string),
    deploymentMeasurementUnits: types.maybe(MeasurementUnitsModel),

    // TODO: Rename model OversightDeploymentModel to DeploymentModel when all settings move from gcs to oversight.
    oversightDeploymentSettings: types.maybeNull(OversightDeploymentModel),

    currentUserAndPermissions: types.maybe(UserAndPermissionsModel),

    fetchCommonSettingsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .volatile(() => ({
    _controlMode: (SHOW_CONTROL_MODE_TOGGLE
      ? (localStorage.getItem(CONTROL_MODE_STORAGE_KEY) as ControlMode) ?? DEFAULT_CONTROL_MODE
      : DEFAULT_CONTROL_MODE) as ControlMode,
  }))
  .views((self) => ({
    // false until the current user's permissions are loaded
    hasPermission(permissionKey: UserPermissionsKeyTypes, permissionType: PermissionTypes): boolean {
      return self.currentUserAndPermissions?.hasPermission(permissionKey, permissionType) ?? false
    },
    get controlMode(): ControlMode {
      return self._controlMode
    },
    get getCentersUrl(): string {
      if (isOversightControl()) {
        return `${self.oversightBaseUrl}deployments/`
      } else {
        return `${self.baseUrl}centers/`
      }
    },
    get authenticateUrl(): string {
      return `${self.oversightBaseUrl}user/permissions`
    },
    get getOversightDeploymentUrl(): string {
      return `${self.oversightBaseUrl}deployments/${CENTER_NAME}`
    },
  }))
  .actions((self) => ({
    setControlMode(mode: ControlMode): void {
      self._controlMode = mode
      localStorage.setItem(CONTROL_MODE_STORAGE_KEY, mode)
      window.location.reload()
    },
    setMeasurementUnits(measurementUnits: MeasurementUnitsEnum): void {
      self.deploymentMeasurementUnits = MeasurementUnitInstances[measurementUnits]

      // Important: initialize MeasurementUnitsService
      MeasurementUnitsService.setMeasurementUnits(self.deploymentMeasurementUnits)
    },
  }))
  .actions((self) => ({
    _loadCenters: flow(function* _loadCenters() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<{id: number, name: string}>>(
          self.getCentersUrl,
          {},
          {fetchOperationName: 'fetch centers'} )
      )
    }),
    _loadUserInfo: flow(function* loadUserInfo() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof UserAndPermissionsModel>>(
          self.authenticateUrl,
          { method: 'GET' },
          { fetchOperationName: 'Authenticate user' },
        ),
      )
    }),
    _loadOversightDeploymentSettings: flow(function* _loadOversightDeploymentSettings() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof OversightDeploymentModel>>(
          self.getOversightDeploymentUrl,
          { method: 'GET' },
          { fetchOperationName: 'Fetch oversight deployment settings' },
        ),
      )
    }),
  }))
  .actions((self) => ({
    initializeCommonSettings: flow(function* initializeCommonSettings() {
        if (self.fetchCommonSettingsStatus.isSuccess) {
          // Don't initialize twice
          return
        }

        self.fetchCommonSettingsStatus = OperationInProgress()
  
        const loadCentersResult = yield* toGenerator(self._loadCenters())
        if (loadCentersResult.error) {
          if (!isOversightControl() && SHOW_CONTROL_MODE_TOGGLE && loadCentersResult.responseStatus === undefined) {
            alert('GCS not answering, forcing Oversight Control')
            self.setControlMode(ControlMode.OversightControl)
            return
          }
          self.fetchCommonSettingsStatus = OperationFailure(loadCentersResult.error)
          return
        }

        const deployment = (isOversightControl())
        ? loadCentersResult.result.find(c => c.name === CENTER_NAME)
        : loadCentersResult.result[0]
        if (deployment === undefined) {
            const message = isOversightControl()
              ? `Can't find center ${CENTER_NAME}`
              : 'GCS must have 1 center location'
            self.fetchCommonSettingsStatus = OperationFailure({name: 'ServerDataError', message: message})
            throw new Error(message)
        }

        self.deploymentId = deployment.id
        self.deploymentName = deployment.name

        const loadCurrentUserAndPermissions = yield* toGenerator(self._loadUserInfo())
        if (loadCurrentUserAndPermissions.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCurrentUserAndPermissions.error)
          return
        }

        const oversightDeploymentSettingsResult = yield* toGenerator(self._loadOversightDeploymentSettings())
        if (oversightDeploymentSettingsResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(oversightDeploymentSettingsResult.error)
          return
        }


        // setting to Imperial Hard Coded as it's not used anymore and was already hard coded in server.
        self.setMeasurementUnits(MeasurementUnitsEnum.Imperial)

        self.currentUserAndPermissions = UserAndPermissionsModel.create(loadCurrentUserAndPermissions.result)

        self.oversightDeploymentSettings = OversightDeploymentModel.create(oversightDeploymentSettingsResult.result)

        self.fetchCommonSettingsStatus = OperationSuccess()
    }),
  }))
